import { db } from '../firebase/config';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { updateProfile } from 'firebase/auth';
import { uploadProfileImage, uploadProfileGif, uploadProfileBackground, deleteImage } from './imageService';

const RECENT_URLS_MAX = 6;

function prependRecentUrl(currentList, newUrl) {
  const list = Array.isArray(currentList) ? [...currentList] : [];
  const filtered = list.filter((u) => u !== newUrl);
  return [newUrl, ...filtered].slice(0, RECENT_URLS_MAX);
}

/**
 * Servicio de gestión de perfiles de usuario
 */

/**
 * Obtener perfil de usuario
 * @param {string} userId - ID del usuario
 * @returns {Promise<object>}
 */
export const getUserProfile = async (userId) => {
  try {
    const userDoc = await getDoc(doc(db, 'users', userId));
    
    if (userDoc.exists()) {
      return {
        success: true,
        data: { id: userDoc.id, ...userDoc.data() }
      };
    } else {
      return {
        success: false,
        error: 'Usuario no encontrado'
      };
    }
  } catch (error) {
    console.error('Error al obtener perfil:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Actualizar perfil de usuario
 * @param {string} userId - ID del usuario
 * @param {object} data - Datos a actualizar
 * @returns {Promise<object>}
 */
export const updateUserProfile = async (userId, data) => {
  try {
    const userRef = doc(db, 'users', userId);
    
    const updateData = {
      ...data,
      updatedAt: serverTimestamp()
    };
    
    await updateDoc(userRef, updateData);
    
    return {
      success: true,
      message: 'Perfil actualizado correctamente'
    };
  } catch (error) {
    console.error('Error al actualizar perfil:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Actualizar foto de perfil
 * @param {File} file - Archivo de imagen
 * @param {string} userId - ID del usuario
 * @param {object} currentUser - Usuario autenticado de Firebase Auth
 * @param {string} oldPhotoURL - URL de la foto anterior (para eliminar)
 * @returns {Promise<object>}
 */
export const updateProfilePhoto = async (file, userId, currentUser, oldPhotoURL = null) => {
  try {
    const isGif = file && file.type === 'image/gif';
    const uploadResult = isGif
      ? await uploadProfileGif(file, userId, 'avatar')
      : await uploadProfileImage(file, userId);

    if (!uploadResult.success) {
      return uploadResult;
    }

    const newPhotoURL = uploadResult.url;
    const userRef = doc(db, 'users', userId);
    let recentAvatarURLs = [];
    try {
      const userSnap = await getDoc(userRef);
      if (userSnap.exists()) {
        recentAvatarURLs = userSnap.data().recentAvatarURLs || [];
      }
    } catch {
      // ignore
    }
    recentAvatarURLs = prependRecentUrl(recentAvatarURLs, newPhotoURL);

    if (currentUser) {
      await updateProfile(currentUser, {
        photoURL: newPhotoURL
      });
    }

    const updateData = {
      photoURL: newPhotoURL,
      recentAvatarURLs,
      updatedAt: serverTimestamp()
    };
    if (isGif) {
      updateData.photoURLIsGif = true;
    } else {
      updateData.photoURLIsGif = false;
    }
    await updateDoc(userRef, updateData);

    if (oldPhotoURL && !oldPhotoURL.includes('default_pic.png')) {
      try {
        await deleteImage(oldPhotoURL);
      } catch (error) {
        console.warn('No se pudo eliminar la foto anterior:', error);
      }
    }

    return {
      success: true,
      photoURL: newPhotoURL,
      message: 'Foto de perfil actualizada'
    };
  } catch (error) {
    console.error('Error al actualizar foto de perfil:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Establecer foto de perfil desde una URL existente (ej. avatar reciente).
 * No sube archivo; actualiza Firestore y Auth.
 * @param {string} userId - ID del usuario
 * @param {string} url - URL de la imagen
 * @param {object} currentUser - Usuario de Firebase Auth
 * @param {string[]} currentRecentAvatarURLs - Lista actual de recientes (para prepend y slice)
 * @returns {Promise<object>}
 */
export const setProfilePhotoFromUrl = async (userId, url, currentUser, currentRecentAvatarURLs = []) => {
  try {
    const userRef = doc(db, 'users', userId);
    const recentAvatarURLs = prependRecentUrl(currentRecentAvatarURLs, url);
    const updateData = {
      photoURL: url,
      photoURLIsGif: url && url.toLowerCase().endsWith('.gif'),
      recentAvatarURLs,
      updatedAt: serverTimestamp()
    };
    await updateDoc(userRef, updateData);
    if (currentUser) {
      await updateProfile(currentUser, { photoURL: url });
    }
    return { success: true, photoURL: url };
  } catch (error) {
    console.error('Error al establecer foto desde URL:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Establecer banner desde una URL existente (ej. banner reciente).
 * @param {string} userId - ID del usuario
 * @param {string} url - URL del banner
 * @param {string[]} currentRecentBannerURLs - Lista actual de recientes
 * @returns {Promise<object>}
 */
export const setBannerFromUrl = async (userId, url, currentRecentBannerURLs = []) => {
  try {
    const userRef = doc(db, 'users', userId);
    const recentBannerURLs = prependRecentUrl(currentRecentBannerURLs, url);
    await updateDoc(userRef, {
      bannerURL: url,
      bannerURLIsGif: url && url.toLowerCase().endsWith('.gif'),
      recentBannerURLs,
      updatedAt: serverTimestamp()
    });
    return { success: true, bannerURL: url };
  } catch (error) {
    console.error('Error al establecer banner desde URL:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Actualizar fondo de perfil (Premium: imagen, video o GIF).
 * @param {string} type - 'image' | 'video' | 'gif'
 * @param {File} file - Archivo a subir
 * @param {string} userId - ID del usuario
 * @param {string|null} oldBackgroundURL - URL anterior para borrar de Storage
 * @returns {Promise<object>}
 */
export const updateProfileBackground = async (type, file, userId, oldBackgroundURL = null) => {
  try {
    const uploadResult = await uploadProfileBackground(file, userId);
    if (!uploadResult.success) return uploadResult;
    const newUrl = uploadResult.url;
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      profileBackgroundType: type,
      profileBackgroundURL: newUrl,
      updatedAt: serverTimestamp()
    });
    if (oldBackgroundURL) {
      try {
        await deleteImage(oldBackgroundURL);
      } catch (e) {
        console.warn('No se pudo eliminar fondo anterior:', e);
      }
    }
    return { success: true, profileBackgroundURL: newUrl };
  } catch (error) {
    console.error('Error al actualizar fondo de perfil:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Eliminar fondo de perfil (Premium).
 * @param {string} userId - ID del usuario
 * @param {string|null} oldBackgroundURL - URL actual
 * @returns {Promise<object>}
 */
export const removeProfileBackground = async (userId, oldBackgroundURL = null) => {
  try {
    if (oldBackgroundURL) {
      try {
        await deleteImage(oldBackgroundURL);
      } catch (e) {
        console.warn('No se pudo eliminar fondo:', e);
      }
    }
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      profileBackgroundType: null,
      profileBackgroundURL: null,
      updatedAt: serverTimestamp()
    });
    return { success: true };
  } catch (error) {
    console.error('Error al eliminar fondo:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Actualizar nombre de usuario
 * @param {string} userId - ID del usuario
 * @param {string} displayName - Nuevo nombre
 * @param {object} currentUser - Usuario autenticado de Firebase Auth
 * @returns {Promise<object>}
 */
export const updateDisplayName = async (userId, displayName, currentUser) => {
  try {
    // Actualizar en Firebase Auth
    if (currentUser) {
      await updateProfile(currentUser, {
        displayName
      });
    }
    
    // Actualizar en Firestore
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      displayName,
      updatedAt: serverTimestamp()
    });
    
    return {
      success: true,
      message: 'Nombre actualizado correctamente'
    };
  } catch (error) {
    console.error('Error al actualizar nombre:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Actualizar biografía
 * @param {string} userId - ID del usuario
 * @param {string} bio - Nueva biografía
 * @returns {Promise<object>}
 */
export const updateBio = async (userId, bio) => {
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      bio: bio || '',
      updatedAt: serverTimestamp()
    });
    
    return {
      success: true,
      message: 'Biografía actualizada'
    };
  } catch (error) {
    console.error('Error al actualizar biografía:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Actualizar banner de perfil
 * @param {File} file - Archivo de imagen del banner
 * @param {string} userId - ID del usuario
 * @param {string} oldBannerURL - URL del banner anterior (para eliminar)
 * @returns {Promise<object>}
 */
export const updateBanner = async (file, userId, oldBannerURL = null) => {
  try {
    const isGif = file && file.type === 'image/gif';
    const uploadResult = isGif
      ? await uploadProfileGif(file, userId, 'banner')
      : await uploadProfileImage(file, userId, 'banner');

    if (!uploadResult.success) {
      return uploadResult;
    }

    const newBannerURL = uploadResult.url;
    const userRef = doc(db, 'users', userId);
    let recentBannerURLs = [];
    try {
      const userSnap = await getDoc(userRef);
      if (userSnap.exists()) {
        recentBannerURLs = userSnap.data().recentBannerURLs || [];
      }
    } catch {
      // ignore
    }
    recentBannerURLs = prependRecentUrl(recentBannerURLs, newBannerURL);

    const updateData = {
      bannerURL: newBannerURL,
      recentBannerURLs,
      updatedAt: serverTimestamp()
    };
    if (isGif) {
      updateData.bannerURLIsGif = true;
    } else {
      updateData.bannerURLIsGif = false;
    }
    await updateDoc(userRef, updateData);

    if (oldBannerURL && !oldBannerURL.includes('unsplash.com')) {
      try {
        await deleteImage(oldBannerURL);
      } catch (error) {
        console.warn('No se pudo eliminar el banner anterior:', error);
      }
    }

    return {
      success: true,
      bannerURL: newBannerURL,
      message: 'Banner actualizado'
    };
  } catch (error) {
    console.error('Error al actualizar banner:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Eliminar banner de perfil (limpia bannerURL y campo legacy banner en Firestore).
 * @param {string} userId - ID del usuario
 * @param {string|null} oldBannerURL - URL del banner actual (para borrar de Storage si aplica)
 * @returns {Promise<object>}
 */
export const removeBanner = async (userId, oldBannerURL = null) => {
  try {
    if (oldBannerURL && !oldBannerURL.includes('unsplash.com') && !oldBannerURL.includes('default')) {
      try {
        await deleteImage(oldBannerURL);
      } catch (error) {
        console.warn('No se pudo eliminar el banner de Storage:', error);
      }
    }

    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      bannerURL: null,
      banner: null,
      updatedAt: serverTimestamp()
    });

    return {
      success: true,
      message: 'Banner eliminado'
    };
  } catch (error) {
    console.error('Error al eliminar banner:', error);
    return {
      success: false,
      error: error.message
    };
  }
};
